import { Pencil } from "lucide-react";
import { useForm } from "react-hook-form";
import { Button } from "../../../ui/common/button";
import { Form } from "../../../ui/common/form";
import { FormInput } from "../../../ui/Input/FormInput/FormInput";
import { TooltipProvider } from "../../../ui/common/tooltip";
import { validateVersionName } from "../../mapping-version/utils/versionName.utils";
import { resolveVersionLabel } from "../utils/versionHistory.utils";
import { InlineEditActions } from "./InlineEditActions";

interface VersionNameFormValues {
  versionName: string;
}

interface VersionNameCellProps {
  versionNumber: number | null;
  versionName: string | null;
  /** false for the synthetic "unversioned" legacy bucket, which has no version to rename. */
  canEdit?: boolean;
  isEditing: boolean;
  /** Every other version's name, for the AC6 duplicate-name client-side check. */
  otherVersionNames: string[];
  isSaving: boolean;
  onStartEdit: () => void;
  /** Returns a field error message on failure, or null on success. */
  onCommit: (value: string) => Promise<string | null>;
  onCancel: () => void;
}

/**
 * Inline editor for one version's name. Owns its own FormProvider, matching the codebase's
 * established row-level-inline-edit convention (see ProcessEditRow.tsx).
 */
export const VersionNameCell = ({
  versionNumber,
  versionName,
  canEdit = true,
  isEditing,
  otherVersionNames,
  isSaving,
  onStartEdit,
  onCommit,
  onCancel
}: VersionNameCellProps) => {
  const label = resolveVersionLabel({ versionName, versionNumber });
  const form = useForm<VersionNameFormValues>({ defaultValues: { versionName: label } });

  if (!canEdit) {
    return <span className="tw-text-base tw-font-semibold tw-text-primary">{label}</span>;
  }

  if (isEditing) {
    const handleSave = form.handleSubmit(async ({ versionName: draft }) => {
      const errorMessage = await onCommit(draft.trim());
      if (errorMessage) {
        form.setError("versionName", { type: "manual", message: errorMessage });
      }
    });

    return (
      // Form = FormProvider — renders no DOM element, table structure stays valid.
      <Form {...form}>
        {/* items-center (not items-start): the Check/X buttons must align with the input's own
            vertical center. [&_label:empty]:tw-hidden collapses FormInput's always-rendered
            FormLabel -- with label={null} it renders as a genuinely empty <label>, which still
            eats ~14px of height above the input inside FormItem's space-y-2 column even though
            it shows nothing. */}
        <div className="tw-flex tw-items-center tw-gap-1.5 [&_label:empty]:tw-hidden">
          <FormInput
            control={form.control}
            name="versionName"
            label={null}
            autoFocus
            disabled={isSaving}
            className="tw-h-8 tw-w-[180px] tw-text-sm"
            aria-label="Version name"
            rules={{ validate: value => validateVersionName(value, otherVersionNames) }}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
              if (e.key === "Escape") onCancel();
            }}
          />
          <TooltipProvider delayDuration={200}>
            <InlineEditActions
              isSaving={isSaving}
              saveLabel="Save version name"
              cancelLabel="Cancel rename"
              onSave={handleSave}
              onCancel={onCancel}
            />
          </TooltipProvider>
        </div>
      </Form>
    );
  }

  return (
    <div className="tw-flex tw-items-center tw-gap-1.5">
      <span className="tw-text-base tw-font-semibold tw-text-primary">{label}</span>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="tw-h-7 tw-w-7 tw-text-muted-foreground"
        aria-label={`Rename ${label}`}
        onClick={onStartEdit}
      >
        <Pencil className="tw-h-3.5 tw-w-3.5" />
      </Button>
    </div>
  );
};
